import { useContext, useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { LogoHorizontal } from "components/Icons";
import { P } from "theme/typography";
import { backend, ianaTimezoneId, isValidEmail, UserForCredential } from "Backend";
import { Link, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import theme from "theme";
import { BroadcastChannelContext, SessionChangedMessage } from "contexts/BroadcastChannelContext";
import { useSnackbar } from "notistack";
import PSelect from "components/PSelect";
import useSearchParamsWithUpdate from "helpers/hooks/useSearchParamsWithUpdate";

interface LoginFormValues {
  email: string;
  password: string;
}

const Login = () => {
  const { searchParams } = useSearchParamsWithUpdate();
  const redirected = searchParams.get("expired") === "true";
  const [submitError, setSubmitError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [requiresUserSelection, setRequiresUserSelection] = useState(false);
  const [users, setUsers] = useState<UserForCredential[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserForCredential | null>(null);
  const broadcastChannelMap = useContext(BroadcastChannelContext);
  const { enqueueSnackbar } = useSnackbar();

  const handleFullSignIn = (email: string, password: string, clientId: number) => {
    const ianaTimezone = ianaTimezoneId();
    setSubmitError("");
    setLoading(true);
    backend
      .loginWithClientId(email, password, clientId, ianaTimezone)
      .then(() => {
        broadcastChannelMap?.session?.postMessage(SessionChangedMessage.LOGGED_IN);
        const destination = decodeURIComponent(searchParams.get("destination") || "");
        navigate(destination || "/lens", { replace: true });
      })
      .catch(() => {
        setSubmitError("There was a problem logging in. Please try again.");
        setLoading(false);
      });
  };

  const handlePreSignIn = async (values: LoginFormValues) => {
    setSubmitError("");
    setLoading(true);
    const email = values.email.toLowerCase().trim();
    const password = values.password;
    backend
      .listUsersForCredentials(email, password)
      .then((response) => {
        if (response.users.length > 1) {
          setLoading(false);
          setRequiresUserSelection(true);
          setUsers(response.users.sort((a, b) => a.clientName.localeCompare(b.clientName)));
        } else if (response.users[0]) {
          handleFullSignIn(email, password, response.users[0].clientId);
        }
      })
      .catch((err) => {
        setSubmitError(
          err.response?.status === 403
            ? "Invalid credentials. Please try again."
            : "There was a problem logging in. Please try again."
        );
        setLoading(false);
      });
  };

  const initialValues: LoginFormValues = { email: "", password: "" };

  const form = useFormik({
    initialValues,
    onSubmit: async (values) => handlePreSignIn(values),
    validate: (values) => {
      const errors: Partial<LoginFormValues> = {};

      if (!values.email) {
        errors.email = "Email can not be empty";
      } else if (!isValidEmail(values.email)) {
        errors.email = "Invalid email address";
      }

      if (!values.password) {
        errors.password = "Password can not be empty";
      }
      return errors;
    },
  });

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        height: "100%",
        width: "100%",
        alignItems: "center",
      }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          width: "100%",
          backgroundColor: theme.palette.background.default,
        }}>
        <Box
          sx={{
            width: "100%",
            maxWidth: 400,
            px: {
              xs: 2,
              md: 0,
            },
          }}>
          <LogoHorizontal size={48} />
          <Box sx={{ display: "flex", flexDirection: "column", mt: 4 }}>
            {redirected && (
              <Box
                sx={{
                  mb: 4,
                }}>
                <Typography variant="sm" color="textSecondary">
                  Your session has expired. Please sign in to continue.
                </Typography>
              </Box>
            )}
            {requiresUserSelection ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}>
                <Box
                  sx={{
                    mb: 2,
                  }}>
                  <Alert
                    severity="success"
                    action={
                      <Button
                        color="inherit"
                        size="small"
                        sx={{
                          transform: "translateY(-2px)",
                        }}
                        onClick={() => {
                          setRequiresUserSelection(false);
                          setUsers([]);
                          setSelectedUser(null);
                          form.setValues(initialValues).catch((error) => {
                            console.error("Error resetting login form: ", error);
                            enqueueSnackbar("Error resetting form", { variant: "error" });
                          });
                          void form.setTouched({});
                        }}>
                        CHANGE
                      </Button>
                    }
                    sx={{
                      mt: 1,
                      alignItems: "center",
                      "& .MuiAlert-message": {
                        p: 0,
                        fontSize: "0.75rem",
                      },
                      "& .MuiAlert-icon": {
                        p: 0,
                      },
                    }}>
                    {form.values.email}
                  </Alert>
                </Box>
                <Box>
                  <P variant="body2">Please choose an Account to continue.</P>
                  {users.length > 4 ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                      }}>
                      <FormControl
                        margin="none"
                        variant="outlined"
                        size="medium"
                        sx={{
                          mt: 2,
                        }}>
                        <InputLabel>Account</InputLabel>
                        <PSelect
                          size="medium"
                          margin="none"
                          label="Account"
                          variant="outlined"
                          value={selectedUser?.clientId || ""}
                          onChange={(e) => {
                            const user = users.find((u) => u.clientId === e.target.value);
                            setSelectedUser(user || null);
                          }}>
                          {users.map((user) => (
                            <MenuItem key={user.clientId} value={user.clientId}>
                              {user.clientName}
                            </MenuItem>
                          ))}
                        </PSelect>
                      </FormControl>
                      <Button
                        color="primary"
                        size="medium"
                        fullWidth
                        type="submit"
                        onClick={() => {
                          if (!selectedUser) return;
                          handleFullSignIn(form.values.email, form.values.password, selectedUser?.clientId);
                        }}
                        disabled={loading || !selectedUser}
                        variant={loading ? "outlined" : "contained"}>
                        Continue
                      </Button>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        mt: 2,
                      }}>
                      {users.map((user) => (
                        <Button
                          key={user.clientId}
                          variant="contained"
                          disabled={loading}
                          onClick={() => {
                            handleFullSignIn(form.values.email, form.values.password, user.clientId);
                          }}>
                          {user.clientName}
                        </Button>
                      ))}
                    </Box>
                  )}

                  <Box
                    sx={{
                      visibility: loading ? "visible" : "hidden",
                      mt: 4,
                    }}>
                    <LinearProgress color="primary" />
                  </Box>
                </Box>
              </Box>
            ) : (
              <form onSubmit={form.handleSubmit}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                    }}>
                    <TextField
                      error={form.touched.email && Boolean(form.errors.email)}
                      helperText={form.touched.email && form.errors.email ? form.errors.email : " "}
                      size="medium"
                      id="email"
                      name="email"
                      label="Email"
                      value={form.values.email}
                      onBlur={form.handleBlur}
                      onChange={form.handleChange}
                      sx={{
                        mb: 1,
                      }}
                      autoComplete="email"
                    />
                    <TextField
                      error={form.touched.password && Boolean(form.errors.password)}
                      helperText={form.touched.password && form.errors.password ? form.errors.password : " "}
                      size="medium"
                      id="password"
                      name="password"
                      label="Password"
                      type="password"
                      value={form.values.password}
                      onBlur={form.handleBlur}
                      onChange={form.handleChange}
                      autoComplete="current-password"
                    />
                  </Box>

                  <Button
                    color="primary"
                    size="medium"
                    fullWidth
                    type="submit"
                    disabled={loading || !form.isValid || !form.dirty}
                    variant={loading ? "outlined" : "contained"}>
                    Sign In
                  </Button>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                    }}>
                    {!loading ? (
                      <Box>
                        <Box
                          hidden={!submitError}
                          sx={{
                            pb: "5px",
                          }}>
                          <Alert
                            severity="error"
                            sx={{
                              "& .MuiAlert-message": {
                                p: 0,
                              },
                              "& .MuiAlert-icon": {
                                p: 0,
                              },
                            }}>
                            {submitError}
                          </Alert>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            mt: submitError ? 0 : "40px",
                          }}>
                          <Button
                            component={Link}
                            to="/forgot-password?person=true"
                            sx={{
                              ...theme.mixins.linkLike,
                            }}
                            variant="text">
                            Forgot password
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <Box
                        hidden={!loading}
                        sx={{
                          mt: 2,
                          pb: "60px",
                        }}>
                        <LinearProgress color="primary" />
                      </Box>
                    )}
                  </Box>
                </Box>
              </form>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
